import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import moment from 'moment-timezone'
import {
  Button,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import API from '../../utils/API'
import { getTimezoneOffsetMinutes } from '../../utils/timezone'
import ScheduleDatePicker from './ScheduleDatePicker'
import ScheduleTimeSlots from './ScheduleTimeSlots'
import classnames from 'classnames'
import {
  SAME_DAY_CONFLICT_CODE,
  SAME_DAY_DUPLICATE_IN_REQUEST_CODE,
  MAX_SESSIONS_PER_WEEK_EXCEEDED_CODE,
  WEEKLY_LIMIT_WARNING_CODE,
  SLOT_NO_LONGER_AVAILABLE_CODE,
} from '@abroad/rest-client'
import useSchedulingRestrictions from '../../hooks/useSchedulingRestrictions'
import WeeklySessionWarningModal from './WeeklySessionWarningModal'
import SchedulingHeader from './components/SchedulingHeader'
import SchedulingMetaPanel from './components/SchedulingMetaPanel'

const isSessionErrorClickable = (code) =>
  code === SAME_DAY_CONFLICT_CODE ||
  code === SAME_DAY_DUPLICATE_IN_REQUEST_CODE ||
  code === MAX_SESSIONS_PER_WEEK_EXCEEDED_CODE

const isSessionRestrictionError = (code) =>
  code === SAME_DAY_CONFLICT_CODE ||
  code === SAME_DAY_DUPLICATE_IN_REQUEST_CODE ||
  code === MAX_SESSIONS_PER_WEEK_EXCEEDED_CODE ||
  code === WEEKLY_LIMIT_WARNING_CODE

/**
 * Conditional Session Scheduling Calendar Component for Admin
 * Handles scheduling for sessions with totalSessions <= 3 and isConditionalSchedulingEnabled = true
 * Shows session index navigation (1, 2, 3) on the left side
 * Timezone is passed as prop from parent (main header)
 */
const ConditionalSessionSchedulingCalendar = ({
  coachDetails = null,
  onScheduleSuccess = null,
  onBack = null,
  timezone, // Timezone from parent (required)
}) => {
  const Error = useErrorService()
  const Notification = useNotificationService()
  const applySessionErrorsRef = useRef(() => {})

  const {
    weeklyModal,
    isConfirmingWeekly,
    closeWeeklyModal,
    handleWeeklyConfirm,
    runScheduling,
  } = useSchedulingRestrictions({
    onSuccess: (data) => {
      setSessionSlotErrors({})
      Notification.showNotification(
        data?.message || 'Your sessions have been scheduled successfully!',
        'success',
      )
      if (onScheduleSuccess) {
        onScheduleSuccess(data?.scheduledSessions)
      }
    },
    onSameDayConflict: (message) => {
      Notification.showNotification(message, 'danger')
    },
    onSessionErrors: (errors) => {
      applySessionErrorsRef.current(errors)
    },
    onError: (message) => {
      Notification.showNotification(message, 'danger')
    },
  })

  // Extract coach information
  const coachId = coachDetails?.coachId
  const scheduleConfig = coachDetails?.scheduleConfig
  const firstSlotDuration = scheduleConfig?.firstSlotDuration
  const otherSlotsDuration = scheduleConfig?.otherSlotsDuration
  const hasGrowthInRemaining = scheduleConfig?.hasGrowthInRemaining
  const sowSessionDuration =
    otherSlotsDuration || coachDetails?.allSessionDuration
  // While growth is still remaining, the first session is the growth session
  const getSessionDuration = (sessionIndex) =>
    hasGrowthInRemaining && Number(sessionIndex) === 1 && firstSlotDuration
      ? firstSlotDuration
      : sowSessionDuration
  const sowId = coachDetails?.sowId
  const totalSessions = coachDetails?.totalSessions || 1
  const sowType = coachDetails?.sowType || coachDetails?.sow?.sowType

  const coachClientName = useMemo(() => {
    const client =
      coachDetails?.clientName || coachDetails?.client?.fullName || '-'
    const coach = coachDetails?.coachName || '-'
    return `${coach} & ${client}`
  }, [coachDetails])

  const getSessionLabel = useCallback(
    (sessionIndex) => {
      const idx = Number(sessionIndex)
      if (sowType === 'QUEST') {
        if (idx === 1) {
          return 'Preparation Session'
        }
        if (idx === 2) {
          return 'Integration Session'
        }
        if (idx === 3) {
          return 'Experience session'
        }
      }
      return `Session ${sessionIndex}`
    },
    [sowType],
  )
  // Extract scheduling conditions
  const schedulingConditions = coachDetails?.schedulingConditions || []
  // Helper function to get date from scheduling condition for a session
  // Converts UTC primary date to user's selected timezone
  const getDateFromCondition = useCallback(
    (sessionIndex) => {
      const condition = schedulingConditions.find(
        (cond) => cond.sessionIndex === sessionIndex,
      )

      if (!condition) {
        return null
      }

      // For all conditions, use primaryDate to determine the month/year
      if (condition.primaryDate) {
        // Convert UTC date to user's selected timezone
        const utcMoment = moment.utc(condition.primaryDate)
        if (timezone && utcMoment.isValid()) {
          return utcMoment.tz(timezone)
        }
        return utcMoment
      }

      return null
    },
    [schedulingConditions, timezone],
  )

  // Helper function to get initial month/year for a session
  // Uses timezone-converted date from condition
  const getInitialMonthForSession = useCallback(
    (sessionIndex) => {
      const condition = schedulingConditions.find(
        (cond) => cond.sessionIndex === sessionIndex,
      )

      // If condition is 'on_or_before', use current month instead of primaryDate month
      if (condition?.condition === 'on_or_before') {
        return moment()
          .tz(timezone || 'UTC')
          .startOf('month')
      }

      const conditionDate = getDateFromCondition(sessionIndex)
      if (conditionDate && conditionDate.isValid()) {
        const conditionMonth = conditionDate.clone().startOf('month')
        // If condition is 'on_or_after' and primaryDate month has already passed, use current month
        if (condition?.condition === 'on_or_after') {
          const currentMonthStart = moment()
            .tz(timezone || 'UTC')
            .startOf('month')
          if (conditionMonth.isBefore(currentMonthStart, 'month')) {
            return currentMonthStart
          }
        }
        return conditionMonth
      }
      return moment()
        .tz(timezone || 'UTC')
        .startOf('month')
    },
    [getDateFromCondition, schedulingConditions, timezone],
  )

  // Helper function to get initial date for a session
  // Converts UTC primary date to user's selected timezone
  // Note: This will be overridden by first slot's date if available
  const getInitialDateForSession = useCallback(
    (sessionIndex) => {
      const condition = schedulingConditions.find(
        (cond) => cond.sessionIndex === sessionIndex,
      )

      if (!condition) {
        return moment()
          .tz(timezone || 'UTC')
          .format('YYYY-MM-DD')
      }

      // For "between" condition, use primaryDate as initial date
      // For "on_or_before" and "on_or_after", use primaryDate
      if (condition.primaryDate) {
        // Convert UTC date to user's selected timezone
        const primaryMoment = moment.utc(condition.primaryDate)
        if (primaryMoment.isValid()) {
          if (timezone) {
            return primaryMoment.tz(timezone).format('YYYY-MM-DD')
          }
          return primaryMoment.format('YYYY-MM-DD')
        }
      }

      return moment()
        .tz(timezone || 'UTC')
        .format('YYYY-MM-DD')
    },
    [schedulingConditions, timezone],
  )

  // State for current session index (1-based)
  const [currentSessionIndex, setCurrentSessionIndex] = useState(1)
  // Store selected slots for each session
  const [sessionSlots, setSessionSlots] = useState({})
  const [sessionSlotErrors, setSessionSlotErrors] = useState({})

  // Initialize with null - will be set to first slot's date after slots load
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [timeSlots, setTimeSlots] = useState([])
  const [availableDays, setAvailableDays] = useState({})
  const [currentMonth, setCurrentMonth] = useState(() =>
    getInitialMonthForSession(1),
  )
  const [isLoadingSlots, setIsLoadingSlots] = useState(false)
  // Initialize preferredDateRef with initial date for preference, but selectedDate starts as null
  // so first slot's date will be selected on initial load
  const preferredDateRef = useRef(getInitialDateForSession(1))
  const [timeFormat] = useState('12h')
  const [isScheduling, setIsScheduling] = useState(false)
  const lastLoadedSessionMonthRef = useRef(null) // Track last loaded sessionIndex + month to prevent duplicate calls
  const lastLoadedSessionIndexRef = useRef(null) // Track last loaded sessionIndex to detect session switches

  const timezoneOffsetMinutes = useMemo(() => {
    if (!timezone) {
      return null
    }
    return getTimezoneOffsetMinutes(timezone)
  }, [timezone])

  // Helper function to convert selected date + UTC slot time to UTC timestamp
  const convertDateAndSlotToUTC = useCallback(
    (selectedDateStr, slotStartUTC, slotEndUTC = null) => {
      if (!selectedDateStr || !slotStartUTC || !timezone) {
        return { start: null, end: null }
      }

      const slotInUserTimezone = moment.utc(slotStartUTC).tz(timezone)
      const timeOfDay = {
        hour: slotInUserTimezone.hour(),
        minute: slotInUserTimezone.minute(),
        second: slotInUserTimezone.second(),
      }

      const selectedDateInTimezone = moment.tz(selectedDateStr, timezone)
      const slotStartInUTC = moment
        .tz(
          [
            selectedDateInTimezone.year(),
            selectedDateInTimezone.month(),
            selectedDateInTimezone.date(),
            timeOfDay.hour,
            timeOfDay.minute,
            timeOfDay.second || 0,
          ],
          timezone,
        )
        .utc()

      let slotEndInUTC = null
      if (slotEndUTC) {
        const slotEndInUserTimezone = moment.utc(slotEndUTC).tz(timezone)
        const endTimeOfDay = {
          hour: slotEndInUserTimezone.hour(),
          minute: slotEndInUserTimezone.minute(),
          second: slotEndInUserTimezone.second(),
        }

        slotEndInUTC = moment
          .tz(
            [
              selectedDateInTimezone.year(),
              selectedDateInTimezone.month(),
              selectedDateInTimezone.date(),
              endTimeOfDay.hour,
              endTimeOfDay.minute,
              endTimeOfDay.second || 0,
            ],
            timezone,
          )
          .utc()
      }

      return {
        start: slotStartInUTC,
        end: slotEndInUTC,
      }
    },
    [timezone],
  )

  const loadMonthSlots = useCallback(
    async (targetMonth, preferredDate, sessionIndex) => {
      if (!coachId || !targetMonth || !sessionIndex) {
        return
      }

      try {
        setIsLoadingSlots(true)
        const normalizedMonth = moment(targetMonth).startOf('month')

        if (!normalizedMonth.isValid()) {
          setIsLoadingSlots(false)
          return
        }

        const monthNumber = normalizedMonth.month() + 1
        const yearNumber = normalizedMonth.year()

        // Check if we've already loaded this combination to prevent duplicate calls
        // But always call if sessionIndex changed (to support switching back to previously viewed sessions)
        const loadKey = `${sessionIndex}-${yearNumber}-${monthNumber}`
        const sessionChanged =
          lastLoadedSessionIndexRef.current !== sessionIndex

        if (!sessionChanged && lastLoadedSessionMonthRef.current === loadKey) {
          setIsLoadingSlots(false)
          return
        }

        const payload = {
          month: Number(monthNumber),
          year: Number(yearNumber),
          sessionIndex: Number(sessionIndex), // Add sessionIndex to payload
        }

        const sessionDuration = getSessionDuration(sessionIndex)
        if (sessionDuration) {
          payload.sowSessionDuration = sessionDuration
        }
        if (sowId) {
          payload.sowId = sowId
        }
        if (timezone) {
          payload.clientTimezone = timezone
        }

        const { data } = await API.ClientAPI.session.getAvailableSlots(
          coachId,
          payload,
        )

        // Mark this combination as loaded
        lastLoadedSessionMonthRef.current = loadKey
        lastLoadedSessionIndexRef.current = sessionIndex

        const daysMap = (data?.days || []).reduce((acc, day) => {
          if (day?.date) {
            acc[day.date] = day
          }
          return acc
        }, {})

        // Replace availableDays for this month (don't merge to avoid stale data)
        setAvailableDays((prev) => {
          // Remove old data for this month to avoid confusion
          const filtered = Object.keys(prev).reduce((acc, date) => {
            const dateMoment = moment(date)
            if (
              dateMoment.month() + 1 === monthNumber &&
              dateMoment.year() === yearNumber
            ) {
              // Don't include old data for this month
              return acc
            }
            acc[date] = prev[date]
            return acc
          }, {})
          // Add new data
          return { ...filtered, ...daysMap }
        })

        const nextSelectableDate = (() => {
          // First, check if there's a saved slot for this session (when revisiting)
          // This takes highest priority - always use saved date if it has slots
          const savedSlot = sessionSlots[sessionIndex]
          if (savedSlot?.date && daysMap[savedSlot.date]?.slots?.length) {
            // Use saved date if it has slots available
            return savedSlot.date
          }

          // If preferredDate exists and has slots, use it (for revisiting sessions with saved slots)
          // preferredDate is set from handleSessionClick when switching to a session with saved slot
          if (preferredDate && daysMap[preferredDate]?.slots?.length) {
            return preferredDate
          }

          // On initial load (selectedDate is null), always use first slot's date
          // This ensures the first available slot's date is selected instead of primary date
          if (!selectedDate) {
            const firstDayWithSlots = Object.values(daysMap).find(
              (day) => day?.slots?.length,
            )
            return firstDayWithSlots?.date || null
          }

          // Fallback: use first slot's date
          const firstDayWithSlots = Object.values(daysMap).find(
            (day) => day?.slots?.length,
          )

          return firstDayWithSlots?.date || null
        })()

        // Only update selectedDate if we have a valid date and it's different
        if (nextSelectableDate && nextSelectableDate !== selectedDate) {
          setSelectedDate(nextSelectableDate)
        }
        // Don't clear selectedSlot here - let the timeSlots useEffect handle it
      } catch (error) {
        Error.showError(error)
        setAvailableDays({})
        setSelectedDate(null)
        setSelectedSlot(null)
      } finally {
        setIsLoadingSlots(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      coachId,
      sowSessionDuration,
      hasGrowthInRemaining,
      firstSlotDuration,
      timezone,
      sowId,
      schedulingConditions,
      sessionSlots,
    ],
  )

  useEffect(() => {
    preferredDateRef.current = selectedDate
  }, [selectedDate])

  // Initialize preferredDateRef with initial date for first load
  useEffect(() => {
    if (preferredDateRef.current === null && currentSessionIndex === 1) {
      const initialDate = getInitialDateForSession(1)
      preferredDateRef.current = initialDate
    }
  }, [currentSessionIndex, getInitialDateForSession])

  // Single useEffect to handle month changes only (session changes handled in handleSessionClick)
  useEffect(() => {
    if (!coachId || !currentMonth || currentSessionIndex === null) {
      return
    }

    // Check cache to prevent duplicate calls
    const monthNumber = currentMonth.month() + 1
    const yearNumber = currentMonth.year()
    const loadKey = `${currentSessionIndex}-${yearNumber}-${monthNumber}`

    // Only load if we haven't already loaded this exact combination
    if (lastLoadedSessionMonthRef.current === loadKey) {
      return
    }

    loadMonthSlots(currentMonth, preferredDateRef.current, currentSessionIndex)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachId, currentMonth, loadMonthSlots])

  useEffect(() => {
    // Auto-select first available date if no date is selected or selected date has no slots
    // But don't override if there's a saved slot for this session (to preserve user's selection when revisiting)
    if (
      currentSessionIndex !== null &&
      (!selectedDate || !availableDays[selectedDate]?.slots?.length) &&
      Object.keys(availableDays).length > 0 &&
      currentMonth
    ) {
      // Check if there's a saved slot for this session
      const savedSlot = sessionSlots[currentSessionIndex]
      if (savedSlot?.date && availableDays[savedSlot.date]?.slots?.length) {
        // Use saved date if it has slots available
        setSelectedDate(savedSlot.date)
        return
      }

      // Otherwise, use first available date in current month
      const firstDayWithSlots = Object.values(availableDays).find((day) => {
        if (!day?.slots?.length || !day?.date) return false
        // Only consider dates in the current month
        const dateMoment = moment(day.date)
        return (
          dateMoment.month() === currentMonth.month() &&
          dateMoment.year() === currentMonth.year()
        )
      })
      if (firstDayWithSlots?.date) {
        setSelectedDate(firstDayWithSlots.date)
        return
      }
    }

    // Check if selectedDate is in the current month being displayed
    if (selectedDate && currentMonth) {
      const selectedDateMoment = moment(selectedDate)
      const isInCurrentMonth =
        selectedDateMoment.month() === currentMonth.month() &&
        selectedDateMoment.year() === currentMonth.year()

      // If selectedDate is not in the current month, clear time slots
      if (!isInCurrentMonth) {
        setTimeSlots([])
        setSelectedSlot(null)
        return
      }
    }

    if (selectedDate && availableDays[selectedDate]?.slots?.length) {
      const slots = availableDays[selectedDate].slots
      setTimeSlots(slots)

      // If we have a saved slot for this session and date, try to restore it
      const savedSlot = sessionSlots[currentSessionIndex]
      if (savedSlot && savedSlot.date === selectedDate && savedSlot.slotStart) {
        // Find the matching slot from the available slots by comparing start time
        // Try exact match first
        let matchingSlot = slots.find(
          (slot) => slot.start === savedSlot.slotStart,
        )

        // If no exact match, try comparing ISO strings (handles different formats)
        if (!matchingSlot) {
          matchingSlot = slots.find((slot) => {
            try {
              const slotStartISO = moment.utc(slot.start).toISOString()
              const savedStartISO = moment
                .utc(savedSlot.slotStart)
                .toISOString()
              return slotStartISO === savedStartISO
            } catch (e) {
              return false
            }
          })
        }

        // If still no match, try comparing by moment objects (more flexible)
        if (!matchingSlot) {
          matchingSlot = slots.find((slot) => {
            try {
              const slotMoment = moment.utc(slot.start)
              const savedMoment = moment.utc(savedSlot.slotStart)
              return slotMoment.isSame(savedMoment)
            } catch (e) {
              return false
            }
          })
        }

        if (matchingSlot) {
          setSelectedSlot(matchingSlot)
        } else {
          // If no match found, clear selectedSlot
          setSelectedSlot(null)
        }
      } else if (!savedSlot || savedSlot.date !== selectedDate) {
        // If no saved slot for this date, clear selectedSlot
        setSelectedSlot(null)
      }
      return
    }

    setTimeSlots([])
    // Clear selectedSlot if no slots available for this date
    if (selectedDate && !availableDays[selectedDate]?.slots?.length) {
      setSelectedSlot(null)
    }
  }, [
    selectedDate,
    availableDays,
    currentSessionIndex,
    sessionSlots,
    currentMonth,
  ])

  // Session switching is now handled in handleSessionClick to avoid multiple useEffects

  const clearSessionError = useCallback((sessionIndex) => {
    if (!sessionIndex) {
      return
    }
    setSessionSlotErrors((prev) => {
      if (!prev[sessionIndex]) {
        return prev
      }
      const next = { ...prev }
      delete next[sessionIndex]
      return next
    })
  }, [])

  const handleDateSelect = (date) => {
    setSelectedDate(date)
    setSelectedSlot(null)
    clearSessionError(currentSessionIndex)
  }

  const handleMonthChange = useCallback((month) => {
    if (!month) {
      return
    }

    const normalizedMonth = moment(month).startOf('month')
    setCurrentMonth((prev) => {
      if (prev && prev.isSame(normalizedMonth, 'month')) {
        return prev
      }

      return normalizedMonth
    })
  }, [])

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot)
    clearSessionError(currentSessionIndex)

    // Immediately save the slot to sessionSlots when selected
    // This ensures changes are saved when going back to update a session
    if (selectedDate && slot) {
      const durationMinutes =
        Number(getSessionDuration(currentSessionIndex)) || 0
      const { start: slotStart, end: slotEndFromConversion } =
        convertDateAndSlotToUTC(selectedDate, slot.start, slot.end)

      const slotEnd = durationMinutes
        ? slotStart.clone().add(durationMinutes, 'minutes')
        : slotEndFromConversion || slotStart.clone()

      setSessionSlots((prev) => ({
        ...prev,
        [currentSessionIndex]: {
          date: selectedDate,
          slotStart: slotStart.toISOString(),
          slotEnd: slotEnd.toISOString(),
        },
      }))
    }
  }

  // Check if a session can be accessed (must book in order, but can go back to update)
  const canAccessSession = (sessionIndex) => {
    // Session 1 is always accessible
    if (sessionIndex === 1) {
      return true
    }

    // For Session 2 and beyond, previous session must be booked
    // But allow going back to update previous sessions
    const previousSessionIndex = sessionIndex - 1
    const previousSessionSlot = sessionSlots[previousSessionIndex]

    // If trying to go forward, previous session must be booked
    if (sessionIndex > currentSessionIndex) {
      return previousSessionSlot?.slotStart !== undefined
    }

    // If going back, always allow (for updating)
    return true
  }

  const applySessionErrors = useCallback(
    (errors) => {
      if (!errors?.length) {
        return
      }

      const errorMap = {}
      errors.forEach(({ sessionIndex, message, code }) => {
        if (sessionIndex != null) {
          errorMap[sessionIndex] = { message, code }
        }
      })
      setSessionSlotErrors(errorMap)

      // A stale slot means the cached month is out of date, so force a reload
      // of availability for the session the user is sent to.
      if (errors.some(({ code }) => code === SLOT_NO_LONGER_AVAILABLE_CODE)) {
        lastLoadedSessionMonthRef.current = null
      }

      const firstSessionIndex = errors[0]?.sessionIndex
      if (firstSessionIndex) {
        handleSessionClick(firstSessionIndex)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionSlots, currentSessionIndex, selectedDate, selectedSlot],
  )

  useEffect(() => {
    applySessionErrorsRef.current = applySessionErrors
  }, [applySessionErrors])

  const handleSessionClick = (sessionIndex) => {
    if (sessionIndex === currentSessionIndex) {
      return
    }

    // Check if session can be accessed
    if (!canAccessSession(sessionIndex)) {
      Error.showError({
        message: `Please book ${getSessionLabel(
          sessionIndex - 1,
        )} first before booking ${getSessionLabel(sessionIndex)}.`,
      })
      return
    }

    // Save current session's selection before switching
    if (selectedDate && selectedSlot) {
      const durationMinutes =
        Number(getSessionDuration(currentSessionIndex)) || 0
      const { start: slotStart, end: slotEndFromConversion } =
        convertDateAndSlotToUTC(
          selectedDate,
          selectedSlot.start,
          selectedSlot.end,
        )

      const slotEnd = durationMinutes
        ? slotStart.clone().add(durationMinutes, 'minutes')
        : slotEndFromConversion || slotStart.clone()

      setSessionSlots((prev) => ({
        ...prev,
        [currentSessionIndex]: {
          date: selectedDate,
          slotStart: slotStart.toISOString(),
          slotEnd: slotEnd.toISOString(),
        },
      }))
    }

    // Handle session switch - update state and load data if needed
    const savedSlot = sessionSlots[sessionIndex]
    let targetDate = null
    let targetMonth = null

    if (savedSlot) {
      // Use saved date if available
      const dateMoment = moment(savedSlot.date)
      if (dateMoment.isValid()) {
        targetDate = savedSlot.date
        targetMonth = dateMoment.clone().startOf('month')
      }
    } else {
      // Use initial date from scheduling condition
      targetDate = getInitialDateForSession(sessionIndex)
      targetMonth = getInitialMonthForSession(sessionIndex)
    }

    // Update session index first
    setCurrentSessionIndex(sessionIndex)
    lastLoadedSessionIndexRef.current = sessionIndex

    // Update date and month
    if (targetDate) {
      setSelectedDate(targetDate)
      preferredDateRef.current = targetDate
    }
    if (targetMonth) {
      // Check if we already have cached data for this session/month combination
      const monthNumber = targetMonth.month() + 1
      const yearNumber = targetMonth.year()
      const loadKey = `${sessionIndex}-${yearNumber}-${monthNumber}`

      // Only reset cache if we don't have this data
      if (lastLoadedSessionMonthRef.current !== loadKey) {
        lastLoadedSessionMonthRef.current = null
      }

      // Update month - this will trigger the useEffect to load data if needed
      setCurrentMonth(targetMonth)
    }
  }

  const handleNext = () => {
    if (!selectedDate || !selectedSlot) {
      Error.showError({ message: 'Please select a date and time slot' })
      return
    }

    // Save current session's selection
    const durationMinutes = Number(getSessionDuration(currentSessionIndex)) || 0
    const { start: slotStart, end: slotEndFromConversion } =
      convertDateAndSlotToUTC(
        selectedDate,
        selectedSlot.start,
        selectedSlot.end,
      )

    const slotEnd = durationMinutes
      ? slotStart.clone().add(durationMinutes, 'minutes')
      : slotEndFromConversion || slotStart.clone()

    const updatedSlots = {
      ...sessionSlots,
      [currentSessionIndex]: {
        date: selectedDate,
        slotStart: slotStart.toISOString(),
        slotEnd: slotEnd.toISOString(),
      },
    }

    setSessionSlots(updatedSlots)

    // Move to next session
    if (currentSessionIndex < totalSessions) {
      const nextSessionIndex = currentSessionIndex + 1

      // Handle session switch - update state and load data if needed (same logic as handleSessionClick)
      const savedSlot = updatedSlots[nextSessionIndex]
      let targetDate = null
      let targetMonth = null

      if (savedSlot) {
        // Use saved date if available
        const dateMoment = moment(savedSlot.date)
        if (dateMoment.isValid()) {
          targetDate = savedSlot.date
          targetMonth = dateMoment.clone().startOf('month')
        }
      } else {
        // Use initial date from scheduling condition
        targetDate = getInitialDateForSession(nextSessionIndex)
        targetMonth = getInitialMonthForSession(nextSessionIndex)
      }

      // Update session index first
      setCurrentSessionIndex(nextSessionIndex)
      lastLoadedSessionIndexRef.current = nextSessionIndex

      // Update date and month
      if (targetDate) {
        setSelectedDate(targetDate)
        preferredDateRef.current = targetDate
      }
      if (targetMonth) {
        // Check if we already have cached data for this session/month combination
        const monthNumber = targetMonth.month() + 1
        const yearNumber = targetMonth.year()
        const loadKey = `${nextSessionIndex}-${yearNumber}-${monthNumber}`

        // Only reset cache if we don't have this data
        if (lastLoadedSessionMonthRef.current !== loadKey) {
          lastLoadedSessionMonthRef.current = null
        }

        // Update month - this will trigger the useEffect to load data if needed
        setCurrentMonth(targetMonth)
      }
    }
  }

  const handleScheduleAll = async () => {
    if (!selectedDate || !selectedSlot) {
      Error.showError({ message: 'Please select a date and time slot' })
      return
    }

    // Save current session's selection
    const durationMinutes = Number(getSessionDuration(currentSessionIndex)) || 0
    const { start: slotStart, end: slotEndFromConversion } =
      convertDateAndSlotToUTC(
        selectedDate,
        selectedSlot.start,
        selectedSlot.end,
      )

    const slotEnd = durationMinutes
      ? slotStart.clone().add(durationMinutes, 'minutes')
      : slotEndFromConversion || slotStart.clone()

    const finalSlots = {
      ...sessionSlots,
      [currentSessionIndex]: {
        date: selectedDate,
        slotStart: slotStart.toISOString(),
        slotEnd: slotEnd.toISOString(),
      },
    }

    // Verify all sessions have slots
    const allSessionsHaveSlots = Array.from(
      { length: totalSessions },
      (_, i) => i + 1,
    ).every((index) => finalSlots[index]?.slotStart)

    if (!allSessionsHaveSlots) {
      Error.showError({
        message: 'Please select slots for all sessions before scheduling.',
      })
      return
    }

    if (!sowId) {
      Notification.showNotification(
        'Unable to find coaching plan details. Please try again later.',
        'danger',
      )
      return
    }

    try {
      setIsScheduling(true)

      // Prepare payload with sessionIndex for each slot
      const timeSlotsPayload = Array.from(
        { length: totalSessions },
        (_, i) => i + 1,
      ).map((sessionIndex) => {
        const slotData = finalSlots[sessionIndex]
        const startMoment = moment.utc(slotData.slotStart)
        const endMoment = moment.utc(slotData.slotEnd)

        const slotPayload = {
          date: slotData.date,
          startTime: startMoment.toISOString(),
          endTime: endMoment.toISOString(),
        }
        if (sowType === 'QUEST') {
          slotPayload.sessionIndex = Number(sessionIndex)
        }
        return slotPayload
      })

      const payload = {
        sowId,
        timeSlots: timeSlotsPayload,
      }

      if (timezone) {
        payload.clientTimezone = timezone
      }

      await runScheduling(
        (schedulePayload) =>
          API.ClientAPI.session.scheduleAllSessions(schedulePayload),
        payload,
      )
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsScheduling(false)
    }
  }

  const isLastSession = currentSessionIndex === totalSessions
  const isSubmitting = isScheduling || isConfirmingWeekly
  const isActionDisabled = isSubmitting || !selectedDate || !selectedSlot
  const schedulePageTitle =
    totalSessions === 1 ? 'Schedule Session' : 'Schedule Multiple Sessions'
  const scheduleButtonText =
    totalSessions === 1 ? 'SCHEDULE' : 'Schedule Multiple Sessions'

  return (
    <div className='session-scheduling-calendar'>
      <SchedulingHeader title={schedulePageTitle} onBack={onBack} />
      <SchedulingMetaPanel
        coachClientName={coachClientName}
        duration={sowSessionDuration}
        totalSessions={totalSessions}
        firstSlotDuration={firstSlotDuration}
        otherSlotsDuration={otherSlotsDuration}
        hasGrowthInRemaining={hasGrowthInRemaining}
        sowType={sowType}
      />
      <div className='session-scheduling-calendar__content mt-30px'>
        <div
          className='border border-dark d-flex flex-column flex-md-row overflow-hidden'
          style={{ minHeight: 490, height: 490 }}>
          {/* Left column: Session index list */}
          <div
            className='flex-shrink-0 p-3 border border-dark border-top-0 border-bottom-0 border-left-0 d-flex flex-column session-scheduling-calendar__sessions-panel'
            style={{ minWidth: 300, width: 300, minHeight: 0 }}>
            <div className='session-index-list h-100'>
              <div className='session-index-list__items'>
                {Array.from({ length: totalSessions }, (_, i) => i + 1).map(
                  (sessionIndex) => {
                    const isActive = sessionIndex === currentSessionIndex
                    const hasSlot = sessionSlots[sessionIndex]?.slotStart
                    const isAccessible = canAccessSession(sessionIndex)
                    const isDisabled = !isAccessible || isSubmitting
                    const sessionError = sessionSlotErrors[sessionIndex]
                    const isRestriction =
                      sessionError &&
                      isSessionRestrictionError(sessionError.code)

                    return (
                      <div
                        key={sessionIndex}
                        role='button'
                        tabIndex={isDisabled ? -1 : 0}
                        className={classnames(
                          'session-index-item py-2 mb-1 w-100 text-center d-flex flex-column align-items-center',
                          {
                            'session-index-item--selected':
                              isActive && !sessionError,
                            'session-index-item--error': sessionError,
                            'session-index-item--restriction':
                              sessionError && isRestriction,
                            'session-index-item--disabled': isDisabled,
                          },
                        )}
                        onClick={() => {
                          if (!isDisabled) {
                            handleSessionClick(sessionIndex)
                          }
                        }}
                        onKeyDown={(event) => {
                          if (
                            !isDisabled &&
                            (event.key === 'Enter' || event.key === ' ')
                          ) {
                            event.preventDefault()
                            handleSessionClick(sessionIndex)
                          }
                        }}>
                        <div className='sa5 font-weight-bold'>
                          {getSessionLabel(sessionIndex)}
                        </div>
                        {sessionError && (
                          <div
                            className={classnames(
                              's6 text-muted mb-0 px-2 session-index-item__error-message',
                              isRestriction
                                ? 'text-saffron-700'
                                : 'text-danger',
                            )}>
                            {sessionError.message}
                            {isSessionErrorClickable(sessionError.code) && (
                              <span>
                                {' '}
                                Click to choose another date or time.
                              </span>
                            )}
                          </div>
                        )}
                        {hasSlot && (
                          <div
                            className={classnames('s6 text-muted mt-2 mb-0', {
                              'text-white-50': isActive && !sessionError,
                              'text-muted': !isActive || sessionError,
                            })}>
                            {moment(sessionSlots[sessionIndex].date).format(
                              'MMM D',
                            )}
                            {', '}
                            {timezone
                              ? moment
                                  .utc(sessionSlots[sessionIndex].slotStart)
                                  .tz(timezone)
                                  .format(
                                    timeFormat === '12h' ? 'h:mm A' : 'HH:mm',
                                  )
                              : timezoneOffsetMinutes !== null
                              ? moment
                                  .utc(sessionSlots[sessionIndex].slotStart)
                                  .utcOffset(timezoneOffsetMinutes)
                                  .format(
                                    timeFormat === '12h' ? 'h:mm A' : 'HH:mm',
                                  )
                              : moment(
                                  sessionSlots[sessionIndex].slotStart,
                                ).format(
                                  timeFormat === '12h' ? 'h:mm A' : 'HH:mm',
                                )}
                          </div>
                        )}
                      </div>
                    )
                  },
                )}
              </div>
            </div>
          </div>

          {/* Middle column: Calendar */}
          <div
            className='flex-fill p-3 border border-dark border-top-0 border-bottom-0 d-flex flex-column'
            style={{ minHeight: 0 }}>
            <ScheduleDatePicker
              currentMonth={currentMonth}
              selectedDate={selectedDate}
              onMonthChange={handleMonthChange}
              onDateSelect={handleDateSelect}
              availableDays={availableDays}
              disablePastDates={true}
              isLoading={isLoadingSlots}
              disableDatesWithoutSlots={true}
            />
          </div>

          {/* Right column: Time slots */}
          <div
            className='flex-fill px-4 py-3 d-flex flex-column overflow-hidden'
            style={{ minHeight: 0 }}>
            <ScheduleTimeSlots
              slots={timeSlots}
              selectedSlot={selectedSlot}
              onSlotSelect={handleSlotSelect}
              initialFormat={timeFormat}
              isLoading={isLoadingSlots}
              timezoneOffsetMinutes={timezoneOffsetMinutes}
              timezone={timezone}
            />
          </div>
        </div>
      </div>

      <div className='d-flex justify-content-end mt-3'>
        <div>
          {isLastSession ? (
            <Button
              variant={isActionDisabled ? 'gray' : 'saffron'}
              size='md'
              className='line-height-normal'
              onClick={handleScheduleAll}
              disabled={isActionDisabled}>
              {isSubmitting ? 'SCHEDULING...' : scheduleButtonText}
            </Button>
          ) : (
            <Button
              variant={isActionDisabled ? 'gray' : 'saffron'}
              size='md'
              className='line-height-normal cursor-pointer'
              onClick={handleNext}
              disabled={isActionDisabled}>
              NEXT
            </Button>
          )}
        </div>
      </div>
      <WeeklySessionWarningModal
        isOpen={weeklyModal.isOpen}
        message={weeklyModal.message}
        onConfirm={handleWeeklyConfirm}
        onCancel={closeWeeklyModal}
        loading={isConfirmingWeekly}
      />
    </div>
  )
}

export default ConditionalSessionSchedulingCalendar
